/**
 * Video Section
 * Блок с видео из YouTube, VK Video или RuTube
 */
import { useState } from 'react';
import { Text, Button, Container } from '../components';

/**
 * Парсит URL видео и возвращает embed URL
 * Поддерживает: YouTube, VK Video, RuTube
 */
function parseVideoUrl(url) {
  if (!url) return null;

  // YouTube
  // https://www.youtube.com/watch?v=VIDEO_ID
  // https://youtu.be/VIDEO_ID
  // https://www.youtube.com/embed/VIDEO_ID
  const youtubeMatch = url.match(
    /(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/
  );
  if (youtubeMatch) {
    return {
      platform: 'youtube',
      videoId: youtubeMatch[1],
      embedUrl: `https://www.youtube.com/embed/${youtubeMatch[1]}`,
      thumbnailUrl: `https://img.youtube.com/vi/${youtubeMatch[1]}/maxresdefault.jpg`,
    };
  }

  // RuTube
  // https://rutube.ru/video/VIDEO_ID/
  // https://rutube.ru/play/embed/VIDEO_ID
  const rutubeMatch = url.match(
    /rutube\.ru\/(?:video|play\/embed)\/([a-zA-Z0-9]+)/
  );
  if (rutubeMatch) {
    return {
      platform: 'rutube',
      videoId: rutubeMatch[1],
      embedUrl: `https://rutube.ru/play/embed/${rutubeMatch[1]}`,
      thumbnailUrl: null, // RuTube doesn't have easy thumbnail API
    };
  }

  // VK Video
  // https://vk.com/video-GROUP_ID_VIDEO_ID or https://vk.com/video@username
  // https://vkvideo.ru/video-GROUP_ID_VIDEO_ID
  // Already embed URL: https://vk.com/video_ext.php?oid=...&id=...&hash=...
  if (url.includes('video_ext.php')) {
    // Already an embed URL
    return {
      platform: 'vkvideo',
      videoId: null,
      embedUrl: url.replace('http://', 'https://'),
      thumbnailUrl: null,
    };
  }

  const vkMatch = url.match(
    /(?:vk\.com|vkvideo\.ru)\/video(-?\d+)_(\d+)/
  );
  if (vkMatch) {
    // VK requires hash for embed, user should paste embed URL directly
    // Return original URL as placeholder
    return {
      platform: 'vkvideo',
      videoId: `${vkMatch[1]}_${vkMatch[2]}`,
      embedUrl: null, // Can't generate without hash
      originalUrl: url,
      thumbnailUrl: null,
    };
  }

  // Unknown - try to use as direct embed
  return {
    platform: 'unknown',
    embedUrl: url,
    thumbnailUrl: null,
  };
}

const aspectRatioClasses = {
  '16:9': 'aspect-video',
  '4:3': 'aspect-[4/3]',
  '1:1': 'aspect-square',
  '9:16': 'aspect-[9/16]',
};

const maxWidthClasses = {
  small: 'max-w-[640px]',
  medium: 'max-w-[854px]',
  large: 'max-w-[1024px]',
  full: 'max-w-full',
};

// Placeholder для превью (html2canvas не может захватить iframe)
function VideoPlaceholder({ videoData, aspectRatio, className = '' }) {
  const thumbnailUrl = videoData?.thumbnailUrl;

  return (
    <div className={`relative ${aspectRatioClasses[aspectRatio]} ${className} rounded-lg overflow-hidden bg-gradient-to-br from-gray-800 to-gray-900`}>
      {thumbnailUrl && (
        <img
          src={thumbnailUrl}
          alt="Video thumbnail"
          className="absolute inset-0 w-full h-full object-cover opacity-80"
        />
      )}
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="w-16 h-16 rounded-full bg-white/90 flex items-center justify-center shadow-lg">
          <svg
            className="w-8 h-8 text-gray-900 ml-1"
            fill="currentColor"
            viewBox="0 0 24 24"
          >
            <path d="M8 5v14l11-7z" />
          </svg>
        </div>
      </div>
      {videoData?.platform && (
        <div className="absolute bottom-3 right-3 px-2 py-1 bg-black/60 rounded text-white text-xs font-medium">
          {videoData.platform === 'youtube' ? 'YouTube' :
           videoData.platform === 'rutube' ? 'RuTube' :
           videoData.platform === 'vkvideo' ? 'VK Video' : 'Video'}
        </div>
      )}
    </div>
  );
}

// Компонент видеоплеера
function VideoPlayer({ videoData, aspectRatio, autoplay, loop, className = '', isPreview = false }) {
  // В режиме превью показываем placeholder (html2canvas не может захватить iframe)
  if (isPreview) {
    return <VideoPlaceholder videoData={videoData} aspectRatio={aspectRatio} className={className} />;
  }

  if (!videoData?.embedUrl) {
    return (
      <div className={`bg-muted flex items-center justify-center rounded-lg ${aspectRatioClasses[aspectRatio]} ${className}`}>
        <div className="text-center text-text-secondary p-4">
          <p className="text-sm">Видео недоступно</p>
          {videoData?.platform === 'vkvideo' && (
            <p className="text-xs mt-2">
              Для VK Video используйте код встраивания из настроек видео
            </p>
          )}
        </div>
      </div>
    );
  }

  let embedUrl = videoData.embedUrl;

  // Add autoplay and loop params
  const params = new URLSearchParams();
  if (autoplay) {
    params.set('autoplay', '1');
    params.set('mute', '1'); // Required for autoplay
  }
  if (loop) {
    params.set('loop', '1');
    if (videoData.platform === 'youtube') {
      params.set('playlist', videoData.videoId); // YouTube requires playlist for loop
    }
  }

  const separator = embedUrl.includes('?') ? '&' : '?';
  if (params.toString()) {
    embedUrl = `${embedUrl}${separator}${params.toString()}`;
  }

  return (
    <div className={`relative ${aspectRatioClasses[aspectRatio]} ${className}`}>
      <iframe
        src={embedUrl}
        className="absolute inset-0 w-full h-full rounded-lg"
        frameBorder="0"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
      />
    </div>
  );
}

// Компонент Lightbox модалки
function VideoLightbox({ isOpen, onClose, videoData, aspectRatio }) {
  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-4xl"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute -top-10 right-0 text-white hover:text-gray-300 transition-colors"
          aria-label="Закрыть"
        >
          <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
        <VideoPlayer
          videoData={videoData}
          aspectRatio={aspectRatio}
          autoplay={true}
        />
      </div>
    </div>
  );
}

export default function VideoSection({ data, styles, isPreview = false }) {
  const [lightboxOpen, setLightboxOpen] = useState(false);

  const layout = data?.layout || 'centered';
  const videoUrl = data?.videoUrl || '';
  const posterImage = data?.posterImage;
  const title = data?.title;
  const subtitle = data?.subtitle;
  const videoPosition = data?.videoPosition || 'left';
  const aspectRatio = data?.aspectRatio || '16:9';
  const maxWidth = data?.maxWidth || 'large';
  const autoplay = data?.autoplay || false;
  const loop = data?.loop || false;
  const buttons = data?.buttons || [];

  const videoData = parseVideoUrl(videoUrl);
  const isVideoLeft = videoPosition === 'left';

  // Получаем превью (пользовательское или автоматическое)
  const thumbnailUrl = posterImage || videoData?.thumbnailUrl;

  // Centered layout
  if (layout === 'centered') {
    return (
      <section
        style={{
          paddingTop: styles?.paddingTop ?? 64,
          paddingBottom: styles?.paddingBottom ?? 64,
          backgroundColor: styles?.backgroundColor || '#ffffff',
        }}
      >
        <Container>
          <div className={`mx-auto ${maxWidthClasses[maxWidth]}`}>
            {/* Заголовок */}
            {(title?.text || title) && (
              <Text
                as="h2"
                data={typeof title === 'object' ? title : { text: title }}
                defaults={{ size: '3xl', weight: 'bold', color: 'inherit' }}
                className="text-center mb-4"
              />
            )}

            {/* Описание */}
            {(subtitle?.text || subtitle) && (
              <Text
                as="p"
                data={typeof subtitle === 'object' ? subtitle : { text: subtitle }}
                defaults={{ size: 'lg', weight: 'normal', color: 'secondary' }}
                className="text-center mb-8 max-w-2xl mx-auto"
              />
            )}

            {/* Видео */}
            <VideoPlayer
              videoData={videoData}
              aspectRatio={aspectRatio}
              autoplay={autoplay}
              loop={loop}
              isPreview={isPreview}
            />
          </div>
        </Container>
      </section>
    );
  }

  // Split layout
  if (layout === 'split') {
    return (
      <section
        style={{
          paddingTop: styles?.paddingTop ?? 64,
          paddingBottom: styles?.paddingBottom ?? 64,
          backgroundColor: styles?.backgroundColor || '#ffffff',
        }}
      >
        <Container>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            {/* Video Side */}
            <div className={isVideoLeft ? 'order-1' : 'order-1 lg:order-2'}>
              <VideoPlayer
                videoData={videoData}
                aspectRatio={aspectRatio}
                autoplay={autoplay}
                loop={loop}
                isPreview={isPreview}
              />
            </div>

            {/* Content Side */}
            <div className={isVideoLeft ? 'order-2' : 'order-2 lg:order-1'}>
              {/* Заголовок */}
              {(title?.text || title) && (
                <Text
                  as="h2"
                  data={typeof title === 'object' ? title : { text: title }}
                  defaults={{ size: '3xl', weight: 'bold', color: 'inherit' }}
                  className="mb-4"
                />
              )}

              {/* Описание */}
              {(subtitle?.text || subtitle) && (
                <Text
                  as="p"
                  data={typeof subtitle === 'object' ? subtitle : { text: subtitle }}
                  defaults={{ size: 'lg', weight: 'normal', color: 'secondary' }}
                  className="mb-6"
                />
              )}

              {/* Кнопки */}
              {buttons.length > 0 && (
                <div className="flex flex-wrap gap-3">
                  {buttons.map((button, index) => {
                    const textData = typeof button.text === 'object' ? button.text : { text: button.text || '' };
                    return (
                      <Button
                        key={button.id || index}
                        href={button.link || '#'}
                        variant={button.variant || 'primary'}
                        size="md"
                      >
                        <Text
                          as="span"
                          data={textData}
                          defaults={{ size: 'base', weight: 'medium', color: 'inherit' }}
                        />
                      </Button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </Container>
      </section>
    );
  }

  // Lightbox layout
  if (layout === 'lightbox') {
    return (
      <section
        style={{
          paddingTop: styles?.paddingTop ?? 64,
          paddingBottom: styles?.paddingBottom ?? 64,
          backgroundColor: styles?.backgroundColor || '#ffffff',
        }}
      >
        <Container>
          <div className={`mx-auto ${maxWidthClasses[maxWidth]}`}>
            {/* Заголовок */}
            {(title?.text || title) && (
              <Text
                as="h2"
                data={typeof title === 'object' ? title : { text: title }}
                defaults={{ size: '3xl', weight: 'bold', color: 'inherit' }}
                className="text-center mb-4"
              />
            )}

            {/* Описание */}
            {(subtitle?.text || subtitle) && (
              <Text
                as="p"
                data={typeof subtitle === 'object' ? subtitle : { text: subtitle }}
                defaults={{ size: 'lg', weight: 'normal', color: 'secondary' }}
                className="text-center mb-8 max-w-2xl mx-auto"
              />
            )}

            {/* Превью с кнопкой Play */}
            <button
              onClick={() => setLightboxOpen(true)}
              className={`relative w-full ${aspectRatioClasses[aspectRatio]} rounded-lg overflow-hidden group cursor-pointer`}
            >
              {thumbnailUrl ? (
                <img
                  src={thumbnailUrl}
                  alt={typeof title === 'object' ? title.text : title || 'Video thumbnail'}
                  className="absolute inset-0 w-full h-full object-cover"
                />
              ) : (
                <div className="absolute inset-0 bg-gradient-to-br from-gray-700 to-gray-900" />
              )}

              {/* Overlay */}
              <div className="absolute inset-0 bg-black/30 group-hover:bg-black/40 transition-colors" />

              {/* Play button */}
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-white/90 group-hover:bg-white group-hover:scale-110 transition-all flex items-center justify-center shadow-lg">
                  <svg
                    className="w-8 h-8 md:w-10 md:h-10 text-gray-900 ml-1"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path d="M8 5v14l11-7z" />
                  </svg>
                </div>
              </div>
            </button>
          </div>
        </Container>

        {/* Lightbox Modal */}
        <VideoLightbox
          isOpen={lightboxOpen}
          onClose={() => setLightboxOpen(false)}
          videoData={videoData}
          aspectRatio={aspectRatio}
        />
      </section>
    );
  }

  return null;
}

// Export parser for use in other places
export { parseVideoUrl };
